import React from 'react';
import styled from 'styled-components';
import {TextFirstLine} from '@styled-icons/fluentui-system-filled/TextFirstLine';
import {Book} from '@styled-icons/boxicons-regular/Book';
import {TextCaseTitle} from '@styled-icons/fluentui-system-filled/TextCaseTitle';
import {CheckmarkCircle} from '@styled-icons/fluentui-system-filled/CheckmarkCircle';
import {H3} from '@bootcamp/web/src/components/Typography';
const HeaderImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 100px;
  margin-right: 12px;
  background: white;
`;

const WordCountIcon = styled(TextCaseTitle)`
  width: 14px;
  height: 14px;
  color: #0075FF;
  margin-right: 8px;
`

const CharacterCountIcon = styled(TextFirstLine)`
  width: 14px;
  height: 14px;
  color: #0075FF;
  margin-right: 8px;
  transform: rotateY(180deg);
`;

const ReadingLevelIcon = styled(Book)`
  width: 14px;
  height: 14px;
  color: #0075FF;
  margin-right: 8px;
`;

const CheckmarkIcon = styled(CheckmarkCircle)`
  width: 14px;
  height: 14px;
  color: #66AC5C;
  background: white;
  border-radius: 100px;
  padding: 1px;
`;

const PassageComparison = ({passageBlockParts}) => {
  // displaying Word Count, Character Count, and Reading Level comparison with official AAMC CARS passages
  // Word count range for AAMC CARS Passages: 497 to 648 words, displayed on a scale from 400 to 800 words, with a green checkmark if the passage falls within the AAMC range
  // Character count range for AAMC CARS Passages: 3044 to 4131 characters, displayed on a scale from 2,000 to 5,000 characters, with a green checkmark if the passage falls within the AAMC range
  // Reading Level range for AAMC CARS Passages: 13.0 to 23.9, displayed on a scale from 0 to 40, with a green checkmark if the passage falls within the AAMC range
  const {wordCount, characterCount, readingLevel} = passageBlockParts;
  const wordCountRange = [497, 648];
  const characterCountRange = [3044, 4131];
  const readingLevelRange = [13.0, 23.9];
  const wordCountScale = [450, 700];
  const characterCountScale = [2500, 4500];
  const readingLevelScale = [6, 30];
  const wordCountCheck = wordCount >= wordCountRange[0] && wordCount <= wordCountRange[1];
  const characterCountCheck = characterCount >= characterCountRange[0] && characterCount <= characterCountRange[1];
  const readingLevelCheck = readingLevel >= readingLevelRange[0] && readingLevel <= readingLevelRange[1];
  return (
    <PassageComparisonContainer>
      <PassageComparisonHeader><HeaderImage src="https://dekni9rgkrcu0.cloudfront.net/assets/icons/bootcamp-glyph-black.png" />Bootcamp vs AAMC Passage Comparison</PassageComparisonHeader>
      <PassageComparisonContent>
        <PassageComparisonRow>
          <PassageComparisonRowLabel>
            <WordCountIcon />
            Word Count
          </PassageComparisonRowLabel>
          <PassageComparisonRowValueContainer>
            <ValueContainer value={wordCount} range={wordCountRange} scale={wordCountScale} />
          </PassageComparisonRowValueContainer>
        </PassageComparisonRow>
        <PassageComparisonRow>
          <PassageComparisonRowLabel><CharacterCountIcon />Character Count</PassageComparisonRowLabel>
          <PassageComparisonRowValueContainer>
            <ValueContainer value={characterCount} range={characterCountRange} scale={characterCountScale} />
          </PassageComparisonRowValueContainer>
        </PassageComparisonRow>
        <PassageComparisonRow>
          <PassageComparisonRowLabel><ReadingLevelIcon />Reading Level</PassageComparisonRowLabel>
          <PassageComparisonRowValueContainer>
            <ValueContainer value={readingLevel} range={readingLevelRange} scale={readingLevelScale} />
          </PassageComparisonRowValueContainer>
        </PassageComparisonRow>
        <BlurbContainer>
          Each <span style={{color: "#66AC5C"}}>green</span> checkmark shows where this passage falls within the <span style={{color: '#3B82F6'}}>range</span> of 134 official AAMC CARS passages we analyzed.
        </BlurbContainer>
      </PassageComparisonContent>
    </PassageComparisonContainer>
  )
}

const BlurbContainer = styled.div`
  font-family: proxima-nova;
  padding: 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, #EEF2FF 0%, #F5F3FF 50%, #FDF2F8 100%);
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
`;

const PassageComparisonContainer = styled.div`
  border: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.colors.neutralsPalette.light};
  border-radius: 8px;
  width: 100%;
  margin-bottom: 16px;
  overflow: hidden;
  user-select: none;
  * {
    font-family: proxima-nova !important;
  }

`;

const PassageComparisonHeader = styled(H3)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.colors.neutralsPalette.light};
  height: 56px;
  background: ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.neutralsPalette.white : theme.colors.neutralsPalette.white};
`;

const PassageComparisonContent = styled.div`
  padding: 16px;
  background: ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.neutralsPalette.white : theme.colors.neutralsPalette.white};
`;

const PassageComparisonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
  ${({theme}) => theme.mediaQueries.tablet} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PassageComparisonRowLabel = styled.div`
  font-family: proxima-nova;
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  color: ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.neutralsPalette.extraDark : theme.colors.neutralsPalette.extraDark};
  min-width: 158px;
`;

const PassageComparisonRowValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ValueContainerLabel = styled.div`
  font-family: proxima-nova;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: ${({theme, color}) => color == 'grey' ? theme.colors.prometricPalette.mcat.grey : '#0075FF'};
  position: absolute;
  ${({position}) => position === 'left' ? `left` : `right`}: 4px;
  top: 5px;
`;

const ValueContainerBase = styled.div`
  width: 100%;
  height: 20px;
  background: #F2F3F5;
  border-radius: 8px;
  position: relative;
`;

const ValueContainerInner = styled.div`
  height: 100%;
  position: absolute;
  border-left: 1px solid #0075FF;
  border-right: 1px solid #0075FF;
  width: ${({width}) => width}%;
  background: rgba(0, 117, 255, 0.1);
  left: ${({offset}) => offset}%;
`;

const CenteredCheckmarkContainer = styled.div`
  position: absolute;
  left: ${({offset}) => offset}%;
  z-index: 1;
  top: -2px;
`;

const ValueContainer = ({value, range, scale}) => {
  const width = (range[1] - range[0]) / (scale[1] - scale[0]) * 100;
  const innerOffset = (100 - width) / 2;
  const valueOffset = (value - scale[0]) / (scale[1] - scale[0]) * 100;
  return (
    <ValueContainerBase>
      <CenteredCheckmarkContainer offset={valueOffset}><CheckmarkIcon/></CenteredCheckmarkContainer>
      <ValueContainerInner width={width} offset={innerOffset}>
        <ValueContainerLabel position={'left'} color={'blue'}>{range[0]}</ValueContainerLabel>
        <ValueContainerLabel position={'right'} color={'blue'}>{range[1]}</ValueContainerLabel>
      </ValueContainerInner>
      <ValueContainerLabel position={'left'} color={'grey'}>{scale[0]}</ValueContainerLabel>
      <ValueContainerLabel position={'right'} color={'grey'}>{scale[1]}</ValueContainerLabel>
    </ValueContainerBase>
  );
}


export default PassageComparison;